<template>
  <div class="note-tile">
    <div class="note-header note-form-header">
      <div class="nav nav-tabs">
        <a class="nav-link" href="#" :class="{active: !previewing}" @click.prevent="togglePreview">
          Write
        </a>
        <a class="nav-link" href="#" :class="{active: previewing}" @click.prevent="togglePreview">
          Preview
        </a>
      </div>
      <div>
        <div class="btn-group" v-if="!commentsMode">
          <a v-for="type in availableTypes" class="btn btn-sm btn-light" :class="{active: type.value === noteType}" @click.prevent="setType(type.value)">
            <i :class="'fa fa-' + type.icon"></i>
            {{type.name}}
          </a>
        </div>
      </div>
    </div>

    <div class="note-form-body">
      <div v-if="autocomplete.show" class="list-group autocomplete" :style="{ 'top': autocomplete.margin + 'px'}">
        <a v-for="option, i in filteredAutocompleteOptions"
          href="#"
          class="list-group-item list-group-item-action"
          :class="{ 'active': i === autocomplete.selectedOption }"
          @click.prevent="replaceWordAtCursor(option.text); hideAutocomplete();"
        >{{option.label}}</a>
        <div class="list-group-item" v-if="filteredAutocompleteOptions.length > 0">
          <small class="help">type to filter, <kbd>↑/↓</kbd> to navigate, <kbd>Enter</kbd> to select, <kbd>Esc</kbd> to cancel</small>
        </div>
      </div>

      <textarea
        v-if="!previewing"
        v-model="markdown"
        ref="textarea"
        placeholder="Add a comment"
        class="form-control"
        @input="handleKey"
        @keydown.up="handleUp"
        @keydown.down="handleDown"
        @keydown.enter.exact="handleEnter"
        @keydown.esc.exact="handleEsc"
      >
      </textarea>
      <div v-if="previewing" v-html="previewHtml" class="note-html note-html-preview"></div>
      <div>
        <table class="table">
          <tbody>
            <tr v-for="file in files" class="attachment-row" :class="{removing: file._action === 'remove'}">
              <td v-if="file.id">
                <img :alt="file.name" :src="file.thumbnail" class="d-inline-block" v-if="file.type === 'image'"/>
                <i class="fa fa-file-o" v-else></i>
              </td>
              <td v-else>
                New file
              </td>
              <td>
                <input class="form-control" type="text" v-model="file.name" @input="onFileNameChange(file)" />
                Action: {{file._action ? file._action : 'keep'}}
              </td>
              <td>
                <a href="#" class="btn btn-danger btn-sm" tabindex="-1" @click.prevent="onRemoveFile(file)" v-if="file._action !== 'remove'">Remove</a>
                <a href="#" class="btn btn-warning btn-sm" tabindex="-1" @click.prevent="onRessurectFile(file)" v-if="file._action === 'remove'">Keep</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <a class="btn btn btn-primary" href="#" @click.prevent="save">
        Comment
      </a>
      <input class="form-control-file mt-3" type="file" value="upload" multiple tabindex="-1" ref="fileInput" @change="onAddFiles" />
      <div class="mt-4">
        <a class="" href="#" :class="{active: helping}" @click.prevent="helping = !helping">
          <small>
            {{helping ? 'Close formatting help' : 'Formatting help'}}
          </small>
        </a>
      </div>
    </div>


    <div v-if="helping" class="md-help">
      <p>
        Comments use <a href="https://en.wikipedia.org/wiki/Markdown" target="_blank">markdown</a> syntax with some extra tweaks.
      </p>
      <table class="table">
        <tbody>
          <tr>
            <td>
              *italics*
              <br/>**bold**
              <br/>***bold italics***
            </td>
            <td>
              <em>italics</em>
              <br/><b>bold</b>
              <br/><b><em>bold italics</em></b>
            </td>
          </tr>
          <tr>
            <td>[a link](https://example.com)</td>
            <td>
              <a href="https://example.com" target="_blank">a link</a>
            </td>
          </tr>
          <tr>
            <td>
              Link to task #T30
            </td>
            <td>
              Link to task <a href="#" onclick="return false" class="task-link"><i class="fa fa-check"></i>#T30</a>
            </td>
          </tr>
          <tr>
            <td>
              Link to chat #C42
            </td>
            <td>
              Link to chat <a href="#" onclick="return false" class="chat-link"><i class="fa fa-comments-o"></i>#C42</a>
            </td>
          </tr>
          <tr>
            <td>
              @SomePerson
              <br/><small>(Don't use spaces)</small>
            </td>
            <td>
              <a href="#" onclick="return false" class="user-mention">@Some Person</a>
              <br/><small>(They will be notified)</small>
            </td>
          </tr>
          <tr>
            <td>
              * one
              <br>* two
              <br>* three
            </td>
            <td>
              <ul>
                <li>one</li>
                <li>two</li>
                <li>three</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>
              # Heading 1
              <br/>## Heading 2
              <br/>### Heading 3
            </td>
            <td>
              <h1>Heading 1</h1>
              <h2>Heading 2</h2>
              <h3>Heading 3</h3>
            </td>
          </tr>
          <tr>
            <td>
              &gt; Something another person wrote
              <br/>
              <br/>
              Something you write
            </td>
            <td>
              <blockquote>
                <p>Something another person wrote</p>
              </blockquote>
              <p>Something you write</p>
            </td>
          </tr>
          <tr>
            <td>
              ```
              <br/>print "source code"
              <br/>2**32 - 1
              <br/>```
            </td>
            <td>
              <pre><code>
print "source code"
2**32 - 1
              </code></pre>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import Vue from 'vue';

  export default {
    props: {
      commentsMode: {
        type: Boolean,
        default: false,
      },
      initialMarkdown: {
        type: String,
        default: '',
      },
      initialType: {
        /* type: Number, */
        /* enforce type when database column is a integer */
        default: 0,
      },
      initialFiles: {
        type: Array,
        default() {
          return [];
        }
      }
    },
    data() {
      return {
        noteType: parseInt(this.initialType),
        markdown: this.initialMarkdown,
        previewing: false,
        helping: false,
        previewHtml: "",
        files: this.initialFiles.map((file) => {
          return JSON.parse(JSON.stringify(file));
        }),
        autocomplete: {
          tasks: [],
          chats: [],
          users: [],
          searchType: "",
          search: "",
          replaceStart: 0,
          replaceEnd: 0,
          show: false,
          selectedOption: 0,
          margin: 0,
        },
      };
    },

    computed: {
      availableTypes() {
        return [
          {name: 'Note', value: 0, icon: 'pencil'},
          {name: 'Email', value: 1, icon: 'envelope'},
          {name: 'Call', value: 2, icon: 'phone'},
          {name: 'Meeting', value: 3, icon: 'user'},
          {name: 'Message', value: 4, icon: 'mobile'},
        ];
      },
      filteredAutocompleteOptions() {
        let options = {
          '#': this.autocomplete.tasks,
          '@': this.autocomplete.users,
        }[this.autocomplete.searchType] || [];

        const search = this.autocomplete.search.toLowerCase();
        if (search) {
          options = options.filter(function(item) {
            return item.search.includes(search);
          });
        }
        return options.slice(0, 5);
      }
    },

    methods: {
      setType(type) {
        this.noteType = type;
      },
      badgeClass(type) {
        var badgeClasses = [
          'fa-pencil',
          'fa-envelope',
          'fa-phone',
          'fa-user',
          'fa-mobile',
        ];

        return badgeClasses[type];
      },
      reset() {
        this.markdown = '';
        this.files = [];
        this.$refs.fileInput.value = '';
        this.previewing = false;
        this.helping = false;
      },
      focus() {
        this.$refs.textarea.focus();
      },
      save() {
        this.$emit('save', {
          markdown: this.markdown,
          noteType: this.noteType,
          files: this.files,
        });
      },
      showAutocomplete() {
        if (this.autocomplete.show) {
          return;
        }

        switch(this.autocomplete.searchType) {
          case "#":
            if (!this.autocomplete.tasks.length) {
              axios.get('/api/tasks/project/' + this.$store.getters.currentProjectId)
                   .then((response) => {
                     this.autocomplete.tasks = response.data.tasks.map((task) => {
                       return {
                         'text': `#${task.slug}`,
                         'search': task.slug.toLowerCase() + task.title.toLowerCase().replace(/\s/g, ''),
                         'label': `${task.slug} ${task.title}`
                       };
                     });
                   });
            }
            break;
          case "@":
            if (!this.autocomplete.users.length) {
              axios.get(`/projects/users/${this.$store.getters.currentProjectId}`)
                   .then((response) => {
                     this.autocomplete.users = response.data.users.map((user) => {
                       return {
                         'text': `@${user.forename}${user.surname}`,
                         'search': user.fullname.toString().toLowerCase().replace(/\s/g, ''),
                         'label': `${user.forename} ${user.surname}`
                       };
                     });
                   });
            }
            break;
          default:
            return
        };

        const currentLine = this.markdown
                                    .substring(0, this.autocomplete.replaceEnd)
                                    .split(/\r\n|\r|\n/)
                                    .length;
        const lineHeight = 1.3 * 16;
        let textareaHeight = this.$refs.textarea.style.height || (14.75 * 16) + 'px';
        textareaHeight = textareaHeight.substring(0, textareaHeight.length - 2);

        this.autocomplete.margin = Math.min((currentLine * lineHeight) + (1.8 * 16), textareaHeight);
        this.autocomplete.show = true;
        this.autocomplete.selectedOption = 0;
      },
      hideAutocomplete() {
        this.autocomplete.show = false;
        this.autocomplete.selectedOption = 0;
        this.focus();
      },
      currentWord(text, position) {
        while (position > 0 && text.charAt(position - 1).match(/\S/)) {
          position--;
        }
        let word = '';
        const wordStart = position;
        while (position < text.length && text.charAt(position).match(/\S/)) {
          word += text.charAt(position);
          position++;
        }
        return [wordStart, word];
      },
      handleKey($event) {
        const [wordStart, currentWord] = this.currentWord(this.markdown, $event.target.selectionStart);
        if (currentWord.startsWith('@')) {
          this.autocomplete.searchType = '@';
          this.autocomplete.search = currentWord.substring(1);
          this.autocomplete.replaceStart = wordStart;
          this.autocomplete.replaceEnd = wordStart + currentWord.length;

          this.showAutocomplete();
          return;
        }

        if (currentWord.startsWith('#')) {
          this.autocomplete.searchType = '#'
          this.autocomplete.search = currentWord.substring(1);
          this.autocomplete.replaceStart = wordStart;
          this.autocomplete.replaceEnd = wordStart + currentWord.length;

          this.showAutocomplete();
          return;
        }

        this.hideAutocomplete();
        return;
      },
      handleEnter(e) {
        if (!this.autocomplete.show) {
          return;
        }
        e.preventDefault();
        const option = this.filteredAutocompleteOptions[this.autocomplete.selectedOption];
        if (option) {
          this.replaceWordAtCursor(option.text);
        }
        this.hideAutocomplete();
      },
      handleUp(e) {
        if (!this.autocomplete.show) {
          return;
        }
        e.preventDefault();
        this.autocomplete.selectedOption = this.autocomplete.selectedOption <= 0 ? 0 : this.autocomplete.selectedOption - 1;
      },
      handleDown(e) {
        if (!this.autocomplete.show) {
          return;
        }
        e.preventDefault();
        this.autocomplete.selectedOption = this.autocomplete.selectedOption >= this.filteredAutocompleteOptions.length - 1 ?
                                           this.filteredAutocompleteOptions.length - 1 :
                                           this.autocomplete.selectedOption + 1;
      },
      handleEsc(e) {
        this.autocomplete.show = false;
      },
      replaceWordAtCursor(text) {
        let before = this.markdown.substring(0, this.autocomplete.replaceStart)
        let after = this.markdown.substring(this.autocomplete.replaceEnd);
        this.markdown = before + text + " " + after;
      },
      togglePreview() {
        let that = this;
        this.previewing = !this.previewing;

        if (this.previewing) {
          var currentProjectId = this.$store.getters.currentProjectId;

          // Tail is defined such that if there is no project id then we pass an empty string otherwise we need to add
          // a backslash before the key. This is because with optional parameters it seems /markdown will match
          // however /markdown/ will not
          var tail = (currentProjectId ? "?project_id=" + currentProjectId : "");

          axios.post('/markdown' + tail, {
            markdown: that.markdown,
          }).then(function(response) {
            that.previewHtml = response.data.html;
          });
        }
      },
      onAddFiles(e) {
        for (let file of e.target.files) {
          this.files.push({
            name: file.name,
            _file: file,
            _action: 'new',
          });
        }
        this.$refs.fileInput.value = '';
      },
      onRemoveFile(file) {
        if (file._action === 'new') {
          this.files = this.files.filter((f) => {
            return f !== file;
          });
          return;
        }

        Vue.set(file, '_action', 'remove');
      },
      onRessurectFile(file) {
        Vue.set(file, '_action', 'edit');
      },
      onFileNameChange(file) {
        if (file._action !== 'remove' && file._action !== 'new') {
          Vue.set(file, '_action', 'edit');
        }
      }
    }
  };
</script>



// WEBPACK FOOTER //
// assets/js/components/NoteForm.vue